// Las pestañas de «Mis torneos», paginadas y filtradas en el API
// (`myOrganizerTournamentsTab` / `myPlayerTournamentsTab`). Qué entra en cada
// una lo decide el servidor: acá no hay que volver a filtrar ni a ordenar.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import { Nullable, Tournament, UserWithToken, WithMeta } from '../api.types';

export type OrganizerTournamentTab = 'UNREPORTED' | 'UPCOMING' | 'HISTORY';
export type PlayerTournamentTab = 'UPCOMING' | 'PAST';

export type TournamentTabFilters = {
  page?: number;
  limit?: number;
  /** Cada palabra tiene que estar en el título, el organizador o el lugar. */
  search?: string;
  formatIds?: number[];
  organizerIds?: number[];
};

export type OrganizerTournamentTabCounts = Record<'unreported' | 'upcoming' | 'history', number>;
export type PlayerTournamentTabCounts = Record<'upcoming' | 'past', number>;

const CARD_FIELDS = `
  id
  title
  dateFrom
  dateTo
  totalPlayers
  rounds
  currentRound
  status
  tournamentType
  gameTypeId
  createdByUserId
  swissId
  withTop
  topTournaments {
    id
    status
  }
  location {
    name
    address
  }
  organizer {
    id
    name
    logoUrl
  }
  format {
    id
    name
  }
`;

const META_FIELDS = `
  meta {
    total
    perPage
    lastPage
  }
`;

export const myOrganizerTournamentsTab = (
  user: Nullable<UserWithToken>,
  tab: OrganizerTournamentTab,
  filters: TournamentTabFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Tournament>>(
    gql`
      query myOrganizerTournamentsTab($tab: OrganizerTournamentTab!, $filters: TournamentTabFilters) {
        data: myOrganizerTournamentsTab(tab: $tab, filters: $filters) {
          data {
            ${CARD_FIELDS}
          }
          ${META_FIELDS}
        }
      }
    `,
    { tab, filters }
  );

export const myOrganizerTournamentTabCounts = (
  user: Nullable<UserWithToken>,
  filters: Omit<TournamentTabFilters, 'page' | 'limit'> = {}
) =>
  getGraphQLQueryClient(user).request<{ data: OrganizerTournamentTabCounts }>(
    gql`
      query myOrganizerTournamentTabCounts($filters: TournamentTabFilters) {
        data: myOrganizerTournamentTabCounts(filters: $filters) {
          unreported
          upcoming
          history
        }
      }
    `,
    { filters }
  );

/**
 * `players` y `results` traen sólo los del usuario: su inscripción (para el
 * estado, ej. dropeado) y su puesto y récord.
 */
export const myPlayerTournamentsTab = (
  user: Nullable<UserWithToken>,
  tab: PlayerTournamentTab,
  filters: TournamentTabFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Tournament>>(
    gql`
      query myPlayerTournamentsTab($tab: PlayerTournamentTab!, $filters: TournamentTabFilters) {
        data: myPlayerTournamentsTab(tab: $tab, filters: $filters) {
          data {
            ${CARD_FIELDS}
            players {
              id
              status
              deckName
              user {
                id
                cossyId
              }
            }
            results {
              id
              userId
              position
              points
              wins
              losses
              draws
            }
          }
          ${META_FIELDS}
        }
      }
    `,
    { tab, filters }
  );

export const myPlayerTournamentTabCounts = (
  user: Nullable<UserWithToken>,
  filters: Omit<TournamentTabFilters, 'page' | 'limit'> = {}
) =>
  getGraphQLQueryClient(user).request<{ data: PlayerTournamentTabCounts }>(
    gql`
      query myPlayerTournamentTabCounts($filters: TournamentTabFilters) {
        data: myPlayerTournamentTabCounts(filters: $filters) {
          upcoming
          past
        }
      }
    `,
    { filters }
  );
